import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import { useWebSocket } from '../WebSocketContext';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Box, 
  LinearProgress, 
  Typography, 
  Toolbar,
  useMediaQuery
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import CloseButton from '../Common/CloseButton';

// Theme colors
const primaryColor = '#5757b0';
const PROGRESS_UPDATE_INTERVAL_MS = 120;
const PDF_CANVAS_SCALE = 1.15;
const PDF_ROTATION_STEP_DEGREES = 90;

GlobalWorkerOptions.workerSrc = pdfjsWorker;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'wrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const PDFContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f0f0f0',
  overflow: 'auto',
  '& iframe': {
    border: 'none',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  '& canvas': {
    width: '100%',
    height: 'auto',
    display: 'block',
    backgroundColor: 'white',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginBottom: '16px',
  },
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(87, 87, 176, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(87, 87, 176, 0.4)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.6)',
    },
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
}));

const PDFViewer = React.forwardRef(({ open, onClose, filename = '', eventId = null, filePath = '' }, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isAndroidChrome = typeof navigator !== 'undefined' &&
    /Android/.test(navigator.userAgent) &&
    /Chrome/.test(navigator.userAgent) &&
    !/EdgA|OPR/.test(navigator.userAgent);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [totalChunks, setTotalChunks] = useState(0);
  const [pdfRotation, setPdfRotation] = useState(0);
  const [pageRendering, setPageRendering] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [renderedPages, setRenderedPages] = useState(0);
  const [pageRenderError, setPageRenderError] = useState(null);
  const containerRef = useRef(null);
  const pagesContainerRef = useRef(null);
  const chunksRef = useRef([]);
  const totalChunksRef = useRef(0);
  const assembledRef = useRef(false);
  const lastProgressUpdateRef = useRef(0);
  const { subscribeToMessages } = useWebSocket();
  const shouldRenderPdfAsPages = isAndroidChrome || pdfRotation !== 0;

  const matchesMessage = useCallback((data = {}) => {
    const msgName = (data.filename || data.name || '').toLowerCase();
    const msgPath = (data.path || '').toLowerCase();
    const fn = (filename || '').toLowerCase();

    if (eventId && data.event_id === eventId) return true;

    if (!eventId) {
      if (fn) {
        if (msgName && msgName === fn) return true;
        if (msgPath && msgPath.endsWith(fn)) return true;
      }
      // If server doesn't send identifiers, accept while viewer is open
      if (!msgName && !msgPath) return true;
    }
    return false;
  }, [eventId, filename]);

  // Cleanup blob URL when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setPdfBlob(null);
      setLoading(false);
      setProgress(0);
      setError(null);
      setTotalChunks(0);
      setPdfRotation(0);
      setPageRendering(false);
      setPageCount(0);
      setRenderedPages(0);
      setPageRenderError(null);
      chunksRef.current = [];
      totalChunksRef.current = 0;
      assembledRef.current = false;
      lastProgressUpdateRef.current = 0;
    } else {
      // When dialog opens, reset state and prepare for new PDF
      setPdfBlob(null);
      setLoading(true); // Start loading immediately when opened
      setProgress(0);
      setError(null);
      setTotalChunks(0);
      setPdfRotation(0);
      setPageRendering(false);
      setPageCount(0);
      setRenderedPages(0);
      setPageRenderError(null);
      chunksRef.current = [];
      totalChunksRef.current = 0;
      assembledRef.current = false;
      lastProgressUpdateRef.current = 0;
    }
  }, [open]);

  // Handle pdfUrl cleanup separately
  useEffect(() => {
    if (!open && pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [open, pdfUrl]);

  useEffect(() => {
    if (!shouldRenderPdfAsPages || !pdfUrl || !open) return;

    let cancelled = false;
    const loadingTask = getDocument({ url: pdfUrl });
    const container = pagesContainerRef.current;

    if (container) {
      container.innerHTML = '';
    }

    setPageRendering(true);
    setPageCount(0);
    setRenderedPages(0);
    setPageRenderError(null);

    loadingTask.promise
      .then(async (pdf) => {
        if (cancelled) return;
        setPageCount(pdf.numPages);

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: PDF_CANVAS_SCALE, rotation: pdfRotation });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (!context) {
            throw new Error('Failed to render PDF page');
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.setAttribute('data-page-number', String(pageNumber));

          if (container) {
            container.appendChild(canvas);
          }

          await page.render({ canvasContext: context, viewport }).promise;
          page.cleanup();
          setRenderedPages(pageNumber);
          await new Promise((resolve) => requestAnimationFrame(resolve));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error rendering PDF pages:', err);
        setPageRenderError(err?.message || 'Failed to render PDF');
      })
      .finally(() => {
        if (!cancelled) {
          setPageRendering(false);
        }
      });

    return () => {
      cancelled = true;
      loadingTask.destroy();
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [shouldRenderPdfAsPages, pdfUrl, open, pdfRotation]);

  const assemblePDF = useCallback((chunks) => {
    if (assembledRef.current) {
      return;
    }

    try {
      assembledRef.current = true;
      // Combine all chunks into a single Uint8Array
      let totalLength = 0;
      chunks.forEach(chunk => {
        totalLength += chunk.length;
      });

      const combined = new Uint8Array(totalLength);
      let offset = 0;
      chunks.forEach(chunk => {
        combined.set(chunk, offset);
        offset += chunk.length;
      });

      // Create blob and URL
      const blob = new Blob([combined], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      setPdfBlob(blob);
      setPdfUrl((previousUrl) => {
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        return url;
      });
      setLoading(false);
    } catch (error) {
      assembledRef.current = false;
      console.error('Error assembling PDF:', error);
      setError('Failed to assemble PDF file');
      setLoading(false);
    }
  }, []);

  // Handle PDF stream messages
  const handlePDFStreamMessage = useCallback((message) => {
    switch (message.status) {
      case 'pdf_stream_start':
        if (matchesMessage(message)) {
          setLoading(true);
          setProgress(0);
          setError(null);
          setPdfRotation(0);
          setPageRenderError(null);
          setTotalChunks(message.total_chunks || 0);
          totalChunksRef.current = message.total_chunks || 0;
          assembledRef.current = false;
          lastProgressUpdateRef.current = 0;
          chunksRef.current = [];
        }
        break;

      case 'pdf_stream_chunk':
        if (matchesMessage(message)) {
          try {
            // Decode base64 chunk
            const binaryString = atob(message.chunk_data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            // Store chunk in ref
            const chunkIndex = message.chunk_number;
            if (!chunksRef.current[chunkIndex]) {
              chunksRef.current[chunkIndex] = bytes;
              
              // Update progress
              const receivedChunks = chunksRef.current.filter(chunk => chunk).length;
              const expectedChunks = totalChunksRef.current || totalChunks;
              const now = Date.now();
              if (expectedChunks > 0 && (message.is_last_chunk || now - lastProgressUpdateRef.current > PROGRESS_UPDATE_INTERVAL_MS)) {
                lastProgressUpdateRef.current = now;
                setProgress((receivedChunks / expectedChunks) * 100);
              }
            }

            // If this is the last chunk, assemble the PDF
            if (message.is_last_chunk) {
              const validChunks = chunksRef.current.filter(chunk => chunk);

              if (validChunks.length > 0) {
                assemblePDF(validChunks);
              } else {
                setError('Failed to assemble PDF - no chunks received');
                setLoading(false);
              }
            }
          } catch (error) {
            console.error('Error processing PDF chunk:', error);
            setError('Failed to process PDF chunk');
          }
        }
        break;

      case 'pdf_stream_complete':
        if (matchesMessage(message)) {
          // Final assembly if not already done
          if (!assembledRef.current && !pdfUrl) {
            const validChunks = chunksRef.current.filter(chunk => chunk);
            if (validChunks.length > 0) {
              assemblePDF(validChunks);
            }
          }
        }
        break;

      case 'error':
        if (matchesMessage(message)) {
          console.warn('[PDFViewer] stream error', message);
          setError(message.message || 'Failed to load PDF');
          setLoading(false);
        }
        break;

      default:
        // No action needed for other statuses
        break;
    }
  }, [totalChunks, pdfUrl, assemblePDF, matchesMessage]);

  useEffect(() => {
    if (!open) return;

    const unsubscribe = subscribeToMessages(['openfile'], (parsedMessage) => {
      if (parsedMessage.data &&
          ['pdf_stream_start', 'pdf_stream_chunk', 'pdf_stream_complete', 'error'].includes(parsedMessage.data.status) &&
          matchesMessage(parsedMessage.data)) {
        handlePDFStreamMessage(parsedMessage.data);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [open, subscribeToMessages, handlePDFStreamMessage, matchesMessage]);

  // Separate useEffect for timeout handling
  useEffect(() => {
    if (!open || !loading || pdfUrl || error) return;

    const timeout = setTimeout(() => {
      setError('Timeout waiting for PDF content. The file might not be a PDF or there might be a server issue.');
      setLoading(false);
    }, 10000); // 10 second timeout

    return () => {
      clearTimeout(timeout);
    };
  }, [open, loading, pdfUrl, error, eventId]);

  // Expose handlePDFStreamMessage through ref
  React.useImperativeHandle(ref, () => ({
    handlePDFStreamMessage
  }), [handlePDFStreamMessage]);

  const downloadPDF = () => {
    if (pdfBlob) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const rotatePDF = () => {
    setPdfRotation((previousRotation) => (previousRotation + PDF_ROTATION_STEP_DEGREES) % 360);
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <PictureAsPdfIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.pdf_viewer_title')} - ${filename}` : t('viewer.pdf_viewer_title')}
          </Typography>
        </Box>
        <CloseButton
          onClick={onClose}
          sx={{
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          size="small"
        />
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadPDF} 
            disabled={!pdfBlob}
            size="small"
            title={t('viewer.download_pdf')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton
            onClick={rotatePDF}
            disabled={!pdfUrl}
            size="small"
            title={t('viewer.rotate_pdf')}
          >
            <Rotate90DegreesCwIcon />
          </IconButton>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.loading_pdf')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
      </ToolbarContainer>

      <DialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <PDFContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 300, mb: 2 }}
              />
              <Typography color="text.secondary">
                {t('viewer.loading_pdf')} {Math.round(progress)}%
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_pdf')}
              </Typography>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Box>
          ) : pdfUrl ? (
            shouldRenderPdfAsPages ? (
              <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                {pageRendering && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('viewer.rendering_pdf')} {renderedPages}/{pageCount || 0}
                    </Typography>
                    <LinearProgress
                      variant={pageCount ? 'determinate' : 'indeterminate'}
                      value={pageCount ? (renderedPages / pageCount) * 100 : undefined}
                    />
                  </Box>
                )}
                {pageRenderError ? (
                  <Typography color="error">
                    {pageRenderError}
                  </Typography>
                ) : (
                  <Box ref={pagesContainerRef} />
                )}
              </Box>
            ) : (
              <iframe
                src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
                width="100%"
                height="100%"
                title="PDF Viewer"
              />
            )
          ) : (
            <Typography color="text.secondary">
              No PDF content available
            </Typography>
          )}
        </PDFContainer>
      </DialogContent>
    </StyledDialog>
  );
});

// Set display name for debugging
PDFViewer.displayName = 'PDFViewer';

export default PDFViewer;
